import type { FileForProcessing, IOrder } from '../../../../Model/index.js'
import type { ISampleMetaData } from '../../../../Model/Sample/ISampleMetaData.js'
import type { CSVMappingFlags, CSVMappingResult, CSVMode, SampleOperationResult } from './CSVModalTypes.js'
import { getSequenceIdFromFilename, sequenceIdFits } from '../../../../Util/SampleFileUtils.js'
import { produceWithPatches } from 'immer'
import type { SampleMetadata } from '../../../../Generated/SampleMetadata.js'
import { buildSampleNameMap, sampleReplicationSuccess } from './SampleOperationSupport.js'

// TODO: handle OpCreateSample generation by looking at "oopsies, these two lovelies refer to the same sequence id"

export const determineSampleOperations = (
  samples: FileForProcessing[],
  metaDatas: ISampleMetaData[],
  csvMapping: CSVMappingResult[],
  flags: CSVMappingFlags,
  mode: CSVMode,
  order?: IOrder
): SampleOperationResult => {
  // Shadowed metadata belongs to negative controls (kept so NC->sample conversion is reversible).
  // It is inactive and must never be matched, updated, or counted during CSV import/update.
  const activeMetaDatas = metaDatas.filter((x) => !x.isShadowed)
  /* ...except when the import turns that control back into a sample, which re-surfaces its metadata */
  const shadowedBySampleId = latestMetadataBySampleId(metaDatas.filter((x) => x.isShadowed))
  switch (mode) {
    case 'PREPARE_METADATA':
      if (order === undefined) {
        throw new Error('Default order id is required for PREPARE_METADATA mode')
      }
      return handlePrepareMetadata(activeMetaDatas, csvMapping, flags, order.id)
    case 'CREATE_SAMPLES':
      return handleCreateAndUpdateSamples(samples, activeMetaDatas, csvMapping, flags, true, false, order, shadowedBySampleId)
    case 'UPDATE_METADATA':
      return handleCreateAndUpdateSamples(samples, activeMetaDatas, csvMapping, flags, false, true, order, shadowedBySampleId)
    default:
      throw new Error(`Unknown mode ${mode}`)
  }
}

const handleCreateAndUpdateSamples = (
  samples: FileForProcessing[],
  metaDatas: ISampleMetaData[],
  csvMapping: CSVMappingResult[],
  flags: CSVMappingFlags,
  allowCreate: boolean,
  matchByName: boolean,
  order?: IOrder,
  shadowedBySampleId: Map<string, ISampleMetaData> = new Map()
): SampleOperationResult => {
  const result: SampleOperationResult = {
    result: [],
    warnings: [],
    errors: [],
    matched: []
  }
  /* prepare data structures */
  const metadataMap: Record<string, ISampleMetaData> = Object.fromEntries(metaDatas.map((x) => [x.customerSampleName, x]))
  const metadataBySampleId = latestMetadataBySampleId(metaDatas)
  const matcher = createRowMatcher(samples, matchByName)
  const sampleIdStorage: Record<string, CSVMappingResult> = {}

  if (matcher.hasDuplicateNames) {
    result.warnings.push({ type: 'WarnDuplicateSampleName' })
  }
  if (matcher.hasDuplicateFiles) {
    result.warnings.push({ type: 'WarnDuplicateFilenames' })
  }
  for (const row of csvMapping) {
    console.info('looking at row', row)
    const existingSample = matcher.match(row)
    if (existingSample === 'no-key') {
      result.errors.push({ type: 'ErrEmptySequenceId' })
      return result
    }
    if (!existingSample) {
      result.warnings.push({ type: 'WarnUnmatchedSequenceId', sequenceId: row.sequenceId, sampleId: row.sampleId })
      continue
    }

    /* handle information about sample duplication */
    if (existingSample.id in sampleIdStorage) {
      if (!sampleReplicationSuccess(result, allowCreate, existingSample, row, order)) {
        return result
      }
      result.matched?.push({ row, item: existingSample, replica: true })
      continue
    }
    sampleIdStorage[existingSample.id] = row
    result.matched?.push({ row, item: existingSample, replica: false })

    /* Types change only where items are still editable, which is where samples are created. */
    const targetType = allowCreate && row.itemType ? row.itemType : existingSample.itemType
    const revivedSample = existingSample.itemType === 'NEGATIVE_CONTROL' && targetType === 'SAMPLE'
    const becomesControl = existingSample.itemType === 'SAMPLE' && targetType === 'NEGATIVE_CONTROL'

    /* Embedded metadata can lag the order's metadata list; without the by-sample lookup a renamed row
       misses the name lookup below and the sample gets a second metadata row. */
    let existingMetadata: ISampleMetaData | undefined =
      existingSample.sampleMetadata ??
      metadataBySampleId.get(existingSample.id) ??
      (revivedSample ? shadowedBySampleId.get(existingSample.id) : undefined)
    if (!existingMetadata) {
      if (allowCreate) {
        existingMetadata = row.customerSampleName?.trim() ? metadataMap[row.customerSampleName?.trim()] : undefined
      } else if (existingSample.itemType === 'SAMPLE') {
        result.errors.push({ type: 'ErrSampleWithoutMetadata', sample: existingSample })
        return result
      }
    }

    if (existingMetadata?.sampleId && existingMetadata?.sampleId !== existingSample?.id) {
      result.errors.push({
        type: 'ErrSampleIdAndMetaIdMismatch',
        sampleName: existingMetadata?.customerSampleName,
        sampleId: row.sampleId,
        boundSampleId: existingMetadata?.sampleId,
        sequenceId: row.sequenceId
      })
      return result
    }

    if (!existingMetadata && metaDatas.find((x) => !x.sampleId)) {
      result.warnings.push({ type: 'WarnMissingMetadataButPresent', sequenceId: row.sequenceId, sampleId: row.sampleId })
    }

    if (!existingSample.orderId || !existingSample.poolId) {
      console.error('Unexpected state: orderId and poolId cannot be null for sample type', existingSample)
      continue
    }

    /* handle sample update */
    if (allowCreate) {
      const [modifiedSample, samplePatches] = produceWithPatches(existingSample, (draft) => {
        mergePrimer(
          draft.forwardPrimer,
          existingMetadata?.forwardPrimerRequested,
          row.forwardPrimer,
          flags.overrideCustomerPrimer,
          (v) => (draft.forwardPrimer = v)
        )
        mergePrimer(
          draft.reversePrimer,
          existingMetadata?.reversePrimerRequested,
          row.reversePrimer,
          flags.overrideCustomerPrimer,
          (v) => (draft.reversePrimer = v)
        )
        if (draft.itemType !== targetType) {
          draft.itemType = targetType
        }
      })
      if (samplePatches.length > 0) {
        result.result.push({
          op: 'UpdateSample',
          sample: modifiedSample,
          poolId: existingSample.poolId,
          orderId: existingSample.orderId
        })
      }
    }
    /* the conversion shadows the sample's metadata; writing it in the same import would undo that */
    if (becomesControl) {
      continue
    }
    /* handle metadata update or creation */
    if (existingMetadata) {
      const [modifiedItem, patches] = produceWithPatches(existingMetadata, (draft) => {
        mergeMetadataForUpdate(draft, row, false, flags.overrideCustomerData)
        if (draft.sampleId !== existingSample.id) {
          draft.sampleId = existingSample.id
        }
      })
      if (patches.length > 0) {
        result.result.push({
          op: 'UpdateMetadata',
          metadata: modifiedItem,
          poolId: existingSample.poolId,
          orderId: existingSample.orderId
        })
      }
    } else {
      /* note: if allowCreate is false, we should never encounter this case since we break above */
      if (!existingSample.orderId) {
        console.error(
          'Encountered an existing samples without an order id, this should not happen! ' +
            'Please report this to the developers. Sample: ',
          existingSample
        )
      }

      /* create metadata only for samples and not for negative controls */
      if (targetType === 'SAMPLE') {
        result.result.push({
          op: 'CreateMetadata',
          metadata: {
            ...row.metadata,
            customerSampleName: row.customerSampleName ?? '',
            sampleId: existingSample.id
          },
          orderId: existingSample.orderId,
          poolId: existingSample.poolId
        })
      }
    }
  }
  return result
}

/** Newest row per linked sample; a sample should only ever have one, but older data can hold two. */
const latestMetadataBySampleId = (metaDatas: ISampleMetaData[]): Map<string, ISampleMetaData> => {
  const bySampleId = new Map<string, ISampleMetaData>()
  metaDatas.forEach((metadata) => {
    if (!metadata.sampleId) {
      return
    }
    const current = bySampleId.get(metadata.sampleId)
    if (!current || (metadata.version ?? '') > (current.version ?? '')) {
      bySampleId.set(metadata.sampleId, metadata)
    }
  })
  return bySampleId
}

const handlePrepareMetadata = (
  metaDatas: ISampleMetaData[],
  csvMapping: CSVMappingResult[],
  flags: CSVMappingFlags,
  defaultOrderId: string
): SampleOperationResult => {
  const metadataMap: Record<string, ISampleMetaData> = metaDatas.reduce(
    (acc, x) => {
      acc[x.customerSampleName] = x
      return acc
    },
    {} as Record<string, ISampleMetaData>
  )
  const result: SampleOperationResult = {
    result: [],
    warnings: [],
    errors: []
  }

  for (const row of csvMapping) {
    if (!row.customerSampleName || !row.customerSampleName.trim()) {
      result.errors.push({ type: 'ErrEmptySampleName' })
      return result
    }

    const existing = metadataMap[row.customerSampleName]
    if (existing) {
      const [modifiedItem, patches] = produceWithPatches(existing, (draft) => {
        mergePrimer(
          draft.forwardPrimerRequested,
          draft.forwardPrimerRequested,
          row.forwardPrimer,
          true,
          (v) => (draft.forwardPrimerRequested = v)
        )
        mergePrimer(
          draft.reversePrimerRequested,
          draft.reversePrimerRequested,
          row.reversePrimer,
          true,
          (v) => (draft.reversePrimerRequested = v)
        )
        mergeMetadataForUpdate(draft, row, true, flags.overrideCustomerData)
      })
      if (patches.length > 0) {
        result.result.push({
          op: 'UpdateMetadata',
          metadata: modifiedItem,
          poolId: undefined,
          orderId: defaultOrderId
        })
      }
    } else {
      result.result.push({
        op: 'CreateMetadata',
        metadata: {
          ...row.metadata,
          customerSampleName: row.customerSampleName,
          forwardPrimerRequested: row.forwardPrimer,
          reversePrimerRequested: row.reversePrimer
        },
        orderId: defaultOrderId,
        poolId: undefined
      })
    }
  }
  return result
}

const isBlank = (value: unknown) => value === undefined || value === null || value === ''

function mergePrimer<T>(
  existingValue: T | undefined,
  customerValue: T | undefined,
  newValue: T | undefined,
  overrideCustomerValues: boolean,
  setter: (v: T | undefined) => void
) {
  /* A stored '' comes back from the backend as undefined (serde omits empty strings); writing '' over it
     would turn every import into a write of every sample, with that sample's possibly stale version. */
  if (isBlank(existingValue) && isBlank(newValue) && (overrideCustomerValues || isBlank(customerValue))) {
    return
  }
  if (existingValue === undefined) {
    if (!overrideCustomerValues && customerValue !== undefined) {
      setter(customerValue)
      return
    }
    if (newValue !== undefined) {
      setter(newValue)
      return
    }
  }
  if (newValue !== undefined) {
    setter(newValue)
    return
  }
}

/** Exported for tests: this is the policy deciding what a CSV row does to stored metadata. */
export function mergeMetadataForUpdate(draft: SampleMetadata, row: CSVMappingResult, applyPrimers: boolean, doOverride: boolean) {
  if (applyPrimers) {
    merge(draft.forwardPrimerRequested, row.forwardPrimer, (v) => (draft.forwardPrimerRequested = v))
    merge(draft.reversePrimerRequested, row.reversePrimer, (v) => (draft.reversePrimerRequested = v))
  }
  merge(draft.sampleCoordinates, row.metadata.sampleCoordinates, (v) => (draft.sampleCoordinates = v), doOverride)
  merge(draft.customerSampleName, row.customerSampleName, (v) => (draft.customerSampleName = v ?? draft.customerSampleName), doOverride)

  merge(draft.samplingDate, row.metadata.samplingDate, (v) => (draft.samplingDate = v), doOverride)
  merge(draft.samplingStart, row.metadata.samplingStart, (v) => (draft.samplingStart = v), doOverride)
  merge(draft.samplingEnd, row.metadata.samplingEnd, (v) => (draft.samplingEnd = v), doOverride)

  merge(draft.matrixType, row.metadata.matrixType, (v) => (draft.matrixType = v), doOverride)
  merge(draft.matrixTypeCustom, row.metadata.matrixTypeCustom, (v) => (draft.matrixTypeCustom = v), doOverride)

  merge(draft.extractionKit, row.metadata.extractionKit, (v) => (draft.extractionKit = v), doOverride)
  merge(draft.extractionKitCustom, row.metadata.extractionKitCustom, (v) => (draft.extractionKitCustom = v), doOverride)

  merge(draft.trapType, row.metadata.trapType, (v) => (draft.trapType = v), doOverride)
  merge(draft.trapTypeCustom, row.metadata.trapTypeCustom, (v) => (draft.trapTypeCustom = v), doOverride)

  merge(draft.collectionType, row.metadata.collectionType, (v) => (draft.collectionType = v), doOverride)
  merge(draft.collectionTypeCustom, row.metadata.collectionTypeCustom, (v) => (draft.collectionTypeCustom = v), doOverride)

  merge(draft.sequencer, row.metadata.sequencer, (v) => (draft.sequencer = v), doOverride)
  merge(draft.sequencerCustom, row.metadata.sequencerCustom, (v) => (draft.sequencerCustom = v), doOverride)

  merge(draft.sequencingChemistry, row.metadata.sequencingChemistry, (v) => (draft.sequencingChemistry = v), doOverride)
  merge(draft.sequencingChemistryCustom, row.metadata.sequencingChemistryCustom, (v) => (draft.sequencingChemistryCustom = v), doOverride)

  merge(draft.sequencingDepth, row.metadata.sequencingDepth, (v) => (draft.sequencingDepth = v), doOverride)

  merge(draft.localityType, row.metadata.localityType, (v) => (draft.localityType = v), doOverride)
  merge(draft.localityTypeCustom, row.metadata.localityTypeCustom, (v) => (draft.localityTypeCustom = v), doOverride)

  merge(draft.habitatType, row.metadata.habitatType, (v) => (draft.habitatType = v), doOverride)
  merge(draft.habitatTypeCustom, row.metadata.habitatTypeCustom, (v) => (draft.habitatTypeCustom = v), doOverride)

  /* special handling for customAttributes */
  if (draft.customAttributes === undefined && row.metadata.customAttributes !== undefined) {
    /* just full on override */
    draft.customAttributes = row.metadata.customAttributes
  } else if (draft.customAttributes !== undefined && row.metadata.customAttributes !== undefined) {
    /* help typescript to not forget narrowed types in foreach closure */
    const existing = draft.customAttributes
    const incoming = row.metadata.customAttributes
    const allKeys = new Set([...Object.keys(draft.customAttributes), ...Object.keys(row.metadata.customAttributes)])
    allKeys.forEach((key) => {
      merge(
        existing[key],
        incoming[key],
        (v) => {
          /* for now, only set the value if it is really set */
          if (v !== undefined) {
            existing[key] = v
          }
        },
        doOverride
      )
    })
  }
  /* nothing to do in all other cases, if new custom is undefined, we don't override here */

  merge(draft.customAttributes, row.metadata.customAttributes, (v) => (draft.customAttributes = v), doOverride)

  /*
   * Applied last so a clear beats anything merged above, and gated on doOverride for the same reason
   * merge() is: with overriding off the import must not touch a value the customer already supplied.
   * Clearing an already-empty field is a no-op, so no separate check for that.
   */
  if (doOverride) {
    row.clearedFields?.forEach((field) => {
      delete draft[field]
    })
  }
}

function merge<T>(existingValue: T | undefined, newValue: T | undefined, setter: (v: T | undefined) => void, doOverride: boolean = true) {
  /* we only actually set the value when we actually have a non-undefined new value and only override existing values
   * if actually requested */
  if ((doOverride || existingValue === undefined) && newValue !== undefined) {
    setter(newValue)
  }
}

type sequenceId = string //auxiliary type for the map for better readability

type RowKeys = Pick<CSVMappingResult, 'sampleId' | 'sequenceId' | 'customerSampleName'>

/**
 * Finds the existing item a row refers to: by spcfy sample id, else by sequence id (file-name prefix), else —
 * where allowed — by customer sample name. `'no-key'` when the row carries none of the keys it may use.
 */
export const createRowMatcher = (samples: FileForProcessing[], matchByName: boolean) => {
  const sampleIdMap: Record<string, FileForProcessing> = Object.fromEntries(samples.map((x) => [x.id, x]))
  const fileMap = buildFileMap(samples)
  const names = buildSampleNameMap(samples)
  const sampleNameMap = names.hasDuplicate ? {} : names.sampleNameMap
  return {
    hasDuplicateNames: names.hasDuplicate,
    hasDuplicateFiles: fileMap.size !== samples.length,
    match: (row: RowKeys): FileForProcessing | null | 'no-key' => {
      if (row.sampleId?.trim()) {
        return sampleIdMap[row.sampleId] ?? null
      }
      if (row.sequenceId?.trim()) {
        return findSampleAndPoolIdInMap(row.sequenceId.trim(), fileMap)
      }
      if (row.customerSampleName?.trim() && matchByName) {
        return sampleNameMap[row.customerSampleName.trim()] ?? null
      }
      return 'no-key'
    }
  }
}

const buildFileMap = (samples: FileForProcessing[]) => {
  return samples.reduce((map, sample) => {
    if (!sample.forwardFile) {
      console.warn('Sample or Control does not have a forward or reverse file', sample)
    } else {
      const sequenceID = getSequenceIdFromFilename(sample.forwardFile.fileName)
      map.set(`${sequenceID}`, sample)
    }
    return map
  }, new Map<sequenceId, FileForProcessing>())
}

const findSampleAndPoolIdInMap = (sequence: string, inputMap: Map<sequenceId, FileForProcessing>): FileForProcessing | null => {
  let result: FileForProcessing | null = null

  inputMap.forEach((value, key) => {
    if (!result && sequenceIdFits(sequence, key)) {
      result = value
    }
  })
  return result
}
